import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, fileUrl, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload, Image as ImageIcon, Package, Eye, EyeOff } from "lucide-react";

const emptyForm = {
  name: "",
  price: "",
  description: "",
  image_path: "",
  active: true,
  sizes: [],
  variants: [],
  variant_label: "Opção",
  payment_link: "",
};

const SIZE_PRESET = ["PP", "P", "M", "G", "GG", "XG", "XGG", "EXG", "EXGG"];

export default function ManageItems() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [togglingId, setTogglingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/items/all");
      setItems(data);
    } catch {
      toast.error("Erro ao carregar itens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name,
      price: String(it.price),
      description: it.description || "",
      image_path: it.image_path || "",
      active: it.active,
      sizes: it.sizes || [],
      variants: it.variants || [],
      variant_label: it.variant_label || "Opção",
      payment_link: it.payment_link || "",
    });
    setModalOpen(true);
  };

  const toggleSize = (s) => {
    setForm((f) => {
      const has = (f.sizes || []).includes(s);
      const next = has ? f.sizes.filter((x) => x !== s) : [...(f.sizes || []), s];
      // preserve preset order first, then any custom sizes
      const preset = SIZE_PRESET.filter((x) => next.includes(x));
      const custom = next.filter((x) => !SIZE_PRESET.includes(x));
      return { ...f, sizes: [...preset, ...custom] };
    });
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Imagem enviada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Preencha nome e valor");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        description: form.description || "",
        image_path: form.image_path || null,
        active: Boolean(form.active),
        sizes: form.sizes || [],
        variants: (form.variants || []).map((v) => v.trim()).filter(Boolean),
        variant_label: (form.variant_label || "Opção").trim() || "Opção",
        payment_link: (form.payment_link || "").trim(),
      };
      if (editingId) {
        await api.put(`/items/${editingId}`, payload);
        toast.success("Item atualizado");
      } else {
        await api.post("/items", payload);
        toast.success("Item cadastrado");
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    try {
      await api.delete(`/items/${id}`);
      toast.success("Item excluído");
      setItems((s) => s.filter((i) => i.id !== id));
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const toggleActive = async (item) => {
    const next = !item.active;
    setTogglingId(item.id);
    // Optimistic update
    setItems((s) => s.map((i) => (i.id === item.id ? { ...i, active: next } : i)));
    try {
      await api.patch(`/items/${item.id}/active`, { active: next });
      toast.success(next ? `"${item.name}" ativado` : `"${item.name}" desativado`);
    } catch (e) {
      // Rollback
      setItems((s) => s.map((i) => (i.id === item.id ? { ...i, active: item.active } : i)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao alterar status");
    } finally {
      setTogglingId(null);
    }
  };

  const filteredItems = items.filter((it) => {
    if (statusFilter === "active") return it.active;
    if (statusFilter === "inactive") return !it.active;
    return true;
  });

  const counts = {
    all: items.length,
    active: items.filter((i) => i.active).length,
    inactive: items.filter((i) => !i.active).length,
  };

  return (
    <div className="space-y-8" data-testid="manage-items">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Catálogo</div>
          <h1 className="font-display text-4xl font-bold mt-2">Itens do evento</h1>
          <p className="text-stone-600 mt-2">Cadastre camisetas, cadernos e outros itens à venda.</p>
        </div>
        <Button
          onClick={openNew}
          disabled={!isAdmin}
          className="bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white rounded-full px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="new-item-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Novo item
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <Package className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhum item cadastrado</div>
            <p className="text-stone-500 text-sm mt-2">Clique em &quot;Novo item&quot; para começar.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="items-filter-tabs">
            {[
              { key: "all", label: "Todos" },
              { key: "active", label: "Ativos" },
              { key: "inactive", label: "Inativos" },
            ].map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setStatusFilter(tab.key)}
                  data-testid={`items-filter-${tab.key}`}
                  className={`h-9 px-4 rounded-full text-sm font-semibold border transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-md"
                      : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20" : "bg-stone-100 text-stone-600"}`}>
                    {counts[tab.key]}
                  </span>
                </button>
              );
            })}
          </div>

          {filteredItems.length === 0 ? (
            <Card className="border-dashed border-stone-300 rounded-2xl">
              <CardContent className="py-12 text-center">
                <Package className="h-8 w-8 text-stone-400 mx-auto" />
                <div className="text-stone-600 mt-3">Nenhum item nesta categoria.</div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredItems.map((it) => (
                <Card
                  key={it.id}
                  className={`border-stone-200 rounded-2xl overflow-hidden card-warm-shadow transition-opacity ${it.active ? "" : "opacity-60 grayscale-[35%]"}`}
                  data-testid={`admin-item-${it.id}`}
                >
                  <div className="aspect-[4/3] bg-stone-100 relative">
                    {it.image_path ? (
                      <img src={fileUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <ImageIcon className="h-10 w-10" />
                      </div>
                    )}
                    {!it.active && (
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-900/80 text-white text-[10px] uppercase tracking-widest font-semibold backdrop-blur-sm">
                        <EyeOff className="h-3 w-3" /> Oculto
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-semibold truncate">{it.name}</h3>
                        <div className="brand-text font-display text-xl font-bold mt-1">{formatBRL(it.price)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Switch
                          checked={it.active}
                          disabled={!isAdmin || togglingId === it.id}
                          onCheckedChange={() => toggleActive(it)}
                          data-testid={`toggle-item-active-${it.id}`}
                          aria-label={it.active ? "Desativar item" : "Ativar item"}
                        />
                        <span className={`text-[10px] uppercase tracking-widest font-semibold ${it.active ? "text-emerald-700" : "text-stone-500"}`}>
                          {it.active ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                    </div>
                    {(it.sizes || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid={`admin-item-sizes-${it.id}`}>
                        {(it.sizes || []).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {it.description && (
                      <p className="text-sm text-stone-600 mt-2 line-clamp-2">{it.description}</p>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(it)}
                        disabled={!isAdmin}
                        className="flex-1 rounded-full disabled:opacity-40"
                        data-testid={`edit-item-${it.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> {isAdmin ? "Editar" : "Ver"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(it.id)}
                        disabled={!isAdmin}
                        className="rounded-full text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800 disabled:opacity-40"
                        data-testid={`delete-item-${it.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="item-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Editar item" : "Novo item"}
            </DialogTitle>
            <DialogDescription>
              Cadastre nome, valor e foto do item que será oferecido no evento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Foto</Label>
              <div className="mt-1 flex items-center gap-4">
                <div className="h-24 w-24 rounded-xl bg-stone-100 border border-stone-200 overflow-hidden flex items-center justify-center shrink-0">
                  {form.image_path ? (
                    <img src={fileUrl(form.image_path)} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-stone-300" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={fileRef}
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                    className="hidden"
                    data-testid="item-image-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="item-upload-btn"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {form.image_path ? "Trocar imagem" : "Enviar imagem"}
                  </Button>
                  <p className="text-xs text-stone-500 mt-1">JPG, PNG ou WebP.</p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="i-name">Nome</Label>
              <Input
                id="i-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Camiseta oficial"
                data-testid="item-name-input"
              />
            </div>

            <div>
              <Label htmlFor="i-price">Valor (R$)</Label>
              <Input
                id="i-price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="49.90"
                data-testid="item-price-input"
              />
            </div>

            <div>
              <Label htmlFor="i-desc">Descrição (opcional)</Label>
              <Textarea
                id="i-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Breve descrição do item"
                rows={3}
                data-testid="item-desc-input"
              />
            </div>

            <div>
              <Label>Tamanhos disponíveis (opcional)</Label>
              <p className="text-xs text-stone-500 mt-0.5">
                Marque os tamanhos oferecidos para este item. Deixe em branco se não se aplica.
              </p>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="item-sizes-selector">
                {Array.from(new Set([...SIZE_PRESET, ...(form.sizes || [])])).map((s) => {
                  const active = (form.sizes || []).includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`item-size-toggle-${s}`}
                      onClick={() => toggleSize(s)}
                      className={`h-9 px-3 rounded-full text-sm font-semibold border transition-all ${
                        active
                          ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-md"
                          : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <Label htmlFor="i-vlabel">Rótulo da opção</Label>
                <Input
                  id="i-vlabel"
                  value={form.variant_label}
                  onChange={(e) => setForm({ ...form, variant_label: e.target.value })}
                  placeholder="Ex: Cardápio"
                  data-testid="item-variant-label-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="i-variants">Opções (uma por linha)</Label>
                <Textarea
                  id="i-variants"
                  rows={3}
                  value={(form.variants || []).join("\n")}
                  onChange={(e) =>
                    setForm({ ...form, variants: e.target.value.split("\n").map((s) => s) })
                  }
                  placeholder={"Isca de carne\nStrogonoff de frango"}
                  data-testid="item-variants-input"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Ex: cardápios de almoço, sabores. Deixe em branco se não se aplica.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="i-payment">Link de pagamento (deste item)</Label>
              <Input
                id="i-payment"
                type="url"
                value={form.payment_link}
                onChange={(e) => setForm({ ...form, payment_link: e.target.value })}
                placeholder="https://mpago.la/..."
                data-testid="item-payment-link-input"
              />
              <p className="text-xs text-stone-500 mt-1">
                O comprador é enviado para este link ao finalizar o pedido deste item. Deixe em branco para usar o link padrão das configurações.
              </p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                data-testid="item-active-input"
              />
              <span className="text-sm">Disponível para venda</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white"
              data-testid="save-item-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
